'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { authenticateAndCreateSession } from '@/utils/auth-helpers';
import { useUserSession } from '@/hooks/use-user-session';
import { useCSRF } from '@/contexts/csrf-context';
import { usePremiumStatus } from '@/hooks/use-premium-status';
import { Button } from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';

const WhyItWorksSection = ({ content, testimonials = [] }) => {
  const locale = useLocale();
  const router = useRouter();
  const userSessionId = useUserSession();
  const { refreshCSRF } = useCSRF();
  const { isPremium, loading: premiumLoading } = usePremiumStatus();
  const [isProcessingSignIn, setIsProcessingSignIn] = useState(false);
  const t = useTranslations('common.header');
  
  if (!content) return null;

  return (
    <>
      {/* Subsection 3: Why It Works */}
      <section id="why-it-works" className="py-16 md:py-20 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            {content.badge && (
              <span
                className="inline-block text-purple-400 text-sm font-semibold tracking-wider uppercase mb-2 text-center w-full"
                style={{ fontFamily: "var(--font-marcellus)" }}
              >
                {content.badge}
              </span>
            )}
            <h2
              className="text-3xl md:text-4xl font-bold text-white mb-4 text-center"
              style={{ fontFamily: "var(--font-marcellus)" }}
            >
              {content.title}
            </h2>
            {content.subtitle && (
              <p
                className="text-base md:text-lg text-gray-300 max-w-4xl mx-auto mb-12 text-center"
                style={{ fontFamily: "var(--font-inter)" }}
              >
                {content.subtitle}
              </p>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {content.features.map((feature, index) => {
                const isImagePath = feature.icon && typeof feature.icon === 'string' && feature.icon.startsWith('/');
                
                return (
                  <div
                    key={index}
                    className="bg-[#100e23]/70 p-6 md:p-8 rounded-xl border border-[#6A3683]/40 shadow-xl shadow-[#6A3683]/20 flex flex-col items-center text-center"
                  >
                    <div className="relative w-20 h-20 md:w-24 md:h-24 mb-4 flex items-center justify-center">
                      {isImagePath ? (
                        <Image
                          src={feature.icon}
                          alt={feature.title}
                          width={96}
                          height={96}
                          className="object-contain w-full h-full"
                          priority={index === 0} // Prioritize first image (above fold)
                          loading={index === 0 ? "eager" : "lazy"}
                          // Removed unoptimized - let Next.js optimize images
                        />
                      ) : (
                        <div className="text-4xl md:text-5xl">{feature.icon}</div>
                      )}
                    </div>
                    <h3
                      className="text-xl md:text-2xl font-bold text-white mb-4"
                      style={{ fontFamily: "var(--font-marcellus)" }}
                    >
                      {feature.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-relaxed" style={{ fontFamily: "var(--font-inter)" }}>
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Separator */}
            <div className="my-12 md:my-16 flex items-center justify-center w-full max-w-2xl mx-auto">
              <div className="grow border-t border-[#A45DE8]/40"></div>
              <span
                className="mx-4 text-2xl md:text-3xl select-none text-[#A45DE8]"
                aria-hidden="true"
                style={{
                  textShadow: "0 0 12px #A45DE8, 0 0 24px #D4B6FF",
                  display: "inline-block"
                }}
              >
                ✧
              </span>
              <div className="grow border-t border-[#A45DE8]/40"></div>
            </div>

            {/* Testimonials */}
            {testimonials && testimonials.length > 0 && (
              <div className="pt-3 md:pt-4 pb-6 md:pb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8">
                  {testimonials.map((testimonial, index) => (
                    <div
                      key={index}
                      className="bg-[#100e23]/70 p-6 rounded-xl border border-[#6A3683]/40 shadow-xl shadow-[#6A3683]/20 flex flex-col items-center text-center transform transition-all duration-300 hover:shadow-[#A45DE8]/30 hover:scale-[1.03]"
                    >
                      <div className="relative w-full mb-4">
                        <span
                          className="absolute -top-2 -left-2 text-4xl text-purple-400/70 opacity-80"
                          style={{ fontFamily: 'var(--font-marcellus)', transform: 'rotate(195deg)' }}
                          aria-hidden="true"
                        >
                          &ldquo;
                        </span>
                        <p className="text-[#FBFBFE]/90 text-sm italic mb-4 leading-relaxed min-h-[100px] flex items-center px-4">
                          {testimonial.quote}
                        </p>
                        <span
                          className="absolute -bottom-2 -right-2 text-4xl text-purple-400/70 opacity-80"
                          style={{ fontFamily: 'var(--font-marcellus)', transform: 'rotate(15deg)' }}
                          aria-hidden="true"
                        >
                          &rdquo;
                        </span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <Image
                          src={testimonial.image}
                          alt={testimonial.alt || testimonial.name}
                          width={32}
                          height={32}
                          priority={index === 0}
                          className="rounded-full object-cover w-8 h-8"
                        />
                        <p
                          className="font-semibold text-purple-300 text-sm"
                          style={{ fontFamily: "var(--font-marcellus)" }}
                        >
                          - {testimonial.name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Subsection 4: The Temple Journey */}
      <section id="temple-journey" className="py-16 md:py-20 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            {content.templeJourney.badge && (
              <span
                className="inline-block text-purple-400 text-sm font-semibold tracking-wider uppercase mb-2 text-center w-full"
                style={{ fontFamily: "var(--font-marcellus)" }}
              >
                {content.templeJourney.badge}
              </span>
            )}
            {/* Centered Title */}
            <h2
              className="text-3xl md:text-4xl font-bold text-white mb-12 text-center"
              style={{ fontFamily: "var(--font-marcellus)" }}
            >
              {content.templeJourney.title}
            </h2>

            {/* Image and Content Layout: 40% / 60% */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12">
              {/* LEFT: Image (40%) */}
              <div className="w-full md:w-[40%] flex-shrink-0">
                <div className="relative w-full aspect-[800/450] rounded-xl overflow-hidden border border-[#6A3683]/40 shadow-xl shadow-[#6A3683]/20">
                  <Image
                    src={content.templeJourney.image}
                    alt="Luna's Temple - Sacred space for spiritual growth"
                    width={800}
                    height={450}
                    className="object-cover w-full h-full"
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, 40vw"
                    // Removed unoptimized - let Next.js optimize for better performance
                  />
                </div>
              </div>

              {/* RIGHT: Content (60%) */}
              <div className="w-full md:w-[60%] text-center md:text-left">
                {content.templeJourney.subtitle && (
                  <p
                    className="text-base md:text-lg text-gray-300 mb-6"
                    style={{ fontFamily: "var(--font-inter)" }}
                  >
                    {content.templeJourney.subtitle}
                  </p>
                )}
                
                <div className="space-y-4 text-base md:text-lg text-gray-300 mb-8" style={{ fontFamily: "var(--font-inter)" }}>
                  {content.templeJourney.description.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>

                <Button
                  variant="primary"
                  onClick={async () => {
                    // If user is not logged in, authenticate first
                    if (!userSessionId) {
                      setIsProcessingSignIn(true);
                      try {
                        const result = await authenticateAndCreateSession(refreshCSRF);
                        
                        if (result.success && result.user) {
                          router.push(`/${locale}/temple`);
                          setIsProcessingSignIn(false);
                          return;
                        } else if (!result.user && !result.error) {
                          // Redirect flow initiated (mobile)
                          setIsProcessingSignIn(false);
                          return;
                        } else if (result.error) {
                          if (result.error.code === 'popup-closed') {
                            console.log('Sign-in popup closed by user.');
                          } else if (result.error.code === 'popup-cancelled') {
                            console.log('Sign-in popup request cancelled.');
                          } else if (result.error.code === 'redirect-cancelled') {
                            console.log('Sign-in redirect operation was cancelled or failed.');
                          } else {
                            console.error('Error during authentication:', result.error);
                            alert(result.error.message || 'An error occurred during the sign-in process. Please try again.');
                          }
                          setIsProcessingSignIn(false);
                          return;
                        }
                      } catch (error) {
                        console.error('Unexpected error during sign-in:', error);
                        alert('An error occurred during the sign-in process. Please try again.');
                        setIsProcessingSignIn(false);
                        return;
                      }
                    } else {
                      // User is already logged in, redirect to temple
                      router.push(`/${locale}/temple`);
                    }
                  }}
                  disabled={isProcessingSignIn}
                  className="inline-flex items-center justify-center h-11 px-5 rounded-lg font-medium text-white transition-all duration-300 ease-in-out shadow-[0_0_15px_rgba(164,93,232,0.2)] oracle-wave hover:shadow-[0_0_25px_rgba(164,93,232,0.65)] animate-oracle-glow text-center"
                >
                  <span className="font-semibold tracking-wide text-base md:text-lg drop-shadow-[0_1px_3px_rgba(21,0,43,0.45)]">
                    {isProcessingSignIn ? (
                      <span className="inline-flex items-center gap-2">
                        <Spinner />
                        <span>{t('buttons.loggingIn')}</span>
                      </span>
                    ) : (
                      content.templeJourney.cta
                    )}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subsection 5: Cross-Sell to Other Tools */}
      <section id="explore-tools" className="py-16 md:py-20 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            {content.exploreTools.badge && (
              <span
                className="inline-block text-purple-400 text-sm font-semibold tracking-wider uppercase mb-2 text-center w-full"
                style={{ fontFamily: "var(--font-marcellus)" }}
              >
                {content.exploreTools.badge}
              </span>
            )}
            <h2
              className="text-3xl md:text-4xl font-bold text-white mb-12 text-center"
              style={{ fontFamily: "var(--font-marcellus)" }}
            >
              {content.exploreTools.title}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {content.exploreTools.tools.map((tool, index) => {
                // Determine if this is a chat tool (check by id or limitColor)
                const isChatTool = tool.id === 'chat' || (tool.limitColor === 'premium' && tool.href === '/pricing');
                
                // For chat tool: premium users go to chatinterface, free users go to pricing
                let toolHref = tool.href;
                if (isChatTool) {
                  toolHref = (isPremium && !premiumLoading) ? '/temple/oracle-readings/chatinterface' : '/pricing';
                }
                
                return (
                <Link
                  key={index}
                  href={`/${locale}${toolHref}`}
                  className="bg-[#100e23]/70 rounded-xl border border-[#6A3683]/40 shadow-xl shadow-[#6A3683]/20 flex flex-col transform transition-all duration-300 hover:shadow-[#A45DE8]/30 hover:scale-[1.03] overflow-hidden"
                >
                  {/* Image */}
                  <div className="relative w-full aspect-[4/3] overflow-hidden">
                    <Image
                      src={tool.image}
                      alt={tool.title}
                      width={400}
                      height={300}
                      className="object-cover w-full h-full"
                      priority={index < 2} // Prioritize first 2 tool images (above fold)
                      loading={index < 2 ? "eager" : "lazy"}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      // Removed unoptimized - let Next.js optimize for better performance
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h3
                      className="text-xl md:text-2xl font-bold text-white mb-3"
                      style={{ fontFamily: "var(--font-marcellus)" }}
                    >
                      {tool.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 mb-4 leading-relaxed flex-grow" style={{ fontFamily: "var(--font-inter)" }}>
                      {tool.description}
                    </p>
                    
                    {/* Limit Badge */}
                    <div className="mb-4">
                      {tool.limitColor === "premium" ? (
                        <span className="inline-block px-3 py-1 text-xs font-semibold bg-gradient-to-r from-[#A45DE8] to-[#6A3683] text-white rounded-full" style={{ fontFamily: "var(--font-inter)" }}>
                          {tool.limit}
                        </span>
                      ) : (
                        <p className={`text-xs md:text-sm ${tool.limitColor === "purple" ? "text-purple-400" : "text-gray-400"}`} style={{ fontFamily: "var(--font-inter)" }}>
                          {tool.limit}
                        </p>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <span className={`text-sm md:text-base font-semibold transition-colors ${tool.ctaStyle === "primary" ? "text-[#A45DE8] hover:text-[#D4B6FF]" : "text-gray-400 hover:text-gray-300"}`} style={{ fontFamily: "var(--font-marcellus)" }}>
                      {tool.cta} →
                    </span>
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default WhyItWorksSection;
